import { reactive } from 'vue';
import { delay } from '@/shared/utils';

/**
 * Delay before showing the "long loading" message, in milliseconds.
 */
const LONG_DELAY = 3000;

/**
 * Period during which the loader remains visible after actual loading is done, in milliseconds.
 * This is mostly to show short bursts of individual loads as one.
 */
const GRACE_PERIOD = 100;

/**
 * Minimal duration a loading message should remain visible, in milliseconds.
 */
const MINIMAL_MESSAGE_DURATION = 2000;

class LoadingService {
  private counter = 0;
  private message = '';

  private longTid: ReturnType<typeof setTimeout>|undefined;
  private canReplaceMessage = true;
  private messageQueue: Array<string> = [];
  private enabled = true;

  enable(): void {
    this.enabled = true;
  }

  disable(): void {
    this.enabled = false;
  }

  isActive(): boolean {
    return this.enabled && this.counter > 0;
  }

  getMessage(): string {
    return this.message;
  }

  start(message?: string): void {
    if (this.counter === 0) {
      this.message = '';
      this.longTid = setTimeout(() => this.pushMessage('general.loadingLong'), LONG_DELAY);
    }
    this.counter++;
    if (message) this.pushMessage(message);
  }

  async stop(): Promise<void> {
    clearTimeout(this.longTid);
    await delay(GRACE_PERIOD);
    this.counter--;
  }

  pushMessage(message: string): void {
    clearTimeout(this.longTid);
    if (this.canReplaceMessage) {
      this.canReplaceMessage = false;
      this.counter++;
      this.showMessage(message);
    } else {
      this.messageQueue.push(message);
    }
  }

  private showMessage(message: string): void {
    this.message = message;
    setTimeout(() => {
      const nextMessage = this.messageQueue.shift();
      if (nextMessage) {
        this.showMessage(nextMessage);
      } else {
        this.canReplaceMessage = true;
        this.counter--;
      }
    }, MINIMAL_MESSAGE_DURATION);
  }
}

export default reactive(new LoadingService());